import React from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Seo } from "./components/Seo";
import { HERO_SETS } from "./content/photos";
import { Container, PageHero, useReveal } from "./components/primitives";
import ConsultationForm from "./forms/ConsultationForm";
import EmployerForm from "./forms/EmployerForm";
import CandidateForm from "./forms/CandidateForm";
import { COMPANY } from "./content/site";

const FORMS = ["consultation", "employer", "candidate"];

export default function Contact() {
  const ref = useReveal(); const [params, setParams] = useSearchParams();
  const tab = FORMS.includes(params.get("form")) ? params.get("form") : "consultation";
  return (
    <div ref={ref}>
      <Seo title="Contact" description="Book a consultation, request staffing or apply as a candidate. TechAI Fortune, Houston, Texas." />
      <PageHero photo={HERO_SETS.contact} eyebrow="Contact" title="Let's turn your next challenge into an outcome." desc="Book a consultation, request staffing, or send us your resume. We respond within one business day." compact />
      <section className="py-20 lg:py-24 bg-light"><Container className="grid lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-4 space-y-8">
          {[
            { icon: Mail, k: "Email", v: <a href={`mailto:${COMPANY.email}`} className="text-[#1A6EFF] hover:underline">{COMPANY.email}</a> },
            { icon: MapPin, k: "Headquarters", v: <>{COMPANY.legal}<br />{COMPANY.address[0]}<br />{COMPANY.address[1]}</> },
            { icon: Clock, k: "Response time", v: "Within one business day" },
          ].map((c) => { const I = c.icon; return (
            <div key={c.k} className="flex items-start gap-4 reveal"><div className="h-11 w-11 rounded-xl bg-white border border-border text-[#1A6EFF] grid place-items-center shrink-0"><I className="h-5 w-5" /></div><div><div className="eyebrow text-muted-foreground">{c.k}</div><div className="text-sm mt-1.5 leading-relaxed">{c.v}</div></div></div>
          ); })}
          <div className="bento dark p-6 text-white reveal"><div className="eyebrow text-[#00D4FF]">What happens next</div><ol className="mt-4 space-y-3 text-sm text-white/75">{["We confirm receipt and review your request.", "A consultant or workforce specialist reaches out within one business day.", "We agree on scope, timeline and a clear next step."].map((s, i) => <li key={s} className="flex gap-3"><span className="font-mono text-[#00D4FF]">0{i + 1}</span>{s}</li>)}</ol></div>
        </aside>
        <div className="lg:col-span-8 bento p-6 md:p-8 reveal">
          <Tabs value={tab} onValueChange={(v) => setParams(v === "consultation" ? {} : { form: v })}>
            <TabsList className="grid grid-cols-3 h-12 rounded-full bg-[#F0F4FF] p-1">
              <TabsTrigger value="consultation" data-testid="contact-tab-consultation" className="nav-label rounded-full">Consultation</TabsTrigger>
              <TabsTrigger value="employer" data-testid="contact-tab-employer" className="nav-label rounded-full">Hire Talent</TabsTrigger>
              <TabsTrigger value="candidate" data-testid="contact-tab-candidate" className="nav-label rounded-full">Find a Role</TabsTrigger>
            </TabsList>
            <TabsContent value="consultation" className="mt-8"><h2 className="font-display text-2xl font-bold mb-6">Book a consultation</h2><ConsultationForm /></TabsContent>
            <TabsContent value="employer" className="mt-8"><h2 className="font-display text-2xl font-bold mb-6">Request staffing</h2><EmployerForm /></TabsContent>
            <TabsContent value="candidate" className="mt-8"><h2 className="font-display text-2xl font-bold mb-6">Apply as a candidate</h2><CandidateForm /></TabsContent>
          </Tabs>
        </div>
      </Container></section>
    </div>
  );
}
